import { observer } from "mobx-react";
import {
  autofocusHeading,
  autofocusText,
  CustomDocumentForm,
  CustomDocumentFreetextBlockForm,
  CustomDocumentPersonalDrawdownContributionBlockForm,
  CustomDocumentShareholdersBlockForm,
  CustomDocumentSignatoryForm,
  CustomDocumentTransactionLogBlockForm,
  getCustomDocumentBlockPanels,
  getCustomDocumentHeaderPanels,
  getCustomDocumentSettingsPanels,
  getCustomDocumentSignaturePanels,
  getSendCustomDocumentTitle,
  isRecipientNameDisabled,
  prepAddBlockDropdown,
  prepAddSenderItems,
  prepAddSignatoryItems,
  quickAdd,
  showAutoTranslateOptions,
  signatoryOptionLabel,
} from "./customDocumentDomain";
import { Box, VBox } from "../../components-v2/Box";
import {
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentSigningStatusLine,
  DocumentViewingStatusLine,
  ReopenDocumentButton,
} from "../common/commonComponents";
import { DetailsPanel, getProjectCompanies, isDocumentSent } from "../common/commonDomain";
import { useTranslation } from "../../stores/RootStoreContext";
import { constant, noop } from "lodash";
import { LocalizedTextEditor } from "../../text/LocalizedTextEditor";
import { MDateInput } from "../../components-v2/DateInput";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { Checkbox, MCheckboxGroup } from "../../components-v2/Checkbox";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { MSelect, Select } from "../../components-v2/Select";
import { MTextInput } from "../../components-v2/TextInput";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";
import { Alert, AlertProps } from "../../components-v2/Alert";
import { Dialog } from "../../components-v2/Dialog";
import { SelectFilesButton } from "../../components-v2/SelectFilesButton";
import { AttachmentsPanel } from "../../components-v2/AttachmentsPanel";
import { when } from "../../utils/functions";
import { prepDocumentAttachmentsPanel, prepUploadDocumentAttachmentsButton } from "../attachments";
import { TMessage } from "../../i18n/tt";
import { Sortable, SortableList, SortableListProps } from "../../components-v2/SortableList";
import { Icon } from "../../components-v2/Icon";
import { RecipientsEditModel } from "../summary/summaryDomain";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";
import { Language } from "../../i18n/languages";
import { Details } from "../../components-v2/Details";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";
import { isPending } from "../../common/common";

export const CustomDocumentView = observer(({ form }: { form: CustomDocumentForm }) => {
  const { lang } = useTranslation();
  const attachmentsPanel = prepDocumentAttachmentsPanel(form.context, form.dispatch);
  const uploadAttachmentsButton = prepUploadDocumentAttachmentsButton(form.context, form.dispatch);
  return (
    <VBox gap={form.context.isLocked ? "xs" : "md"}>
      {form.isSentForSigning ? (
        <DocumentSigningStatusLine context={form.context} dispatch={form.dispatch} />
      ) : (
        <DocumentViewingStatusLine context={form.context} dispatch={form.dispatch} />
      )}
      <DetailsSummaryList
        dispatch={form.dispatch}
        isLocked={false}
        panels={getCustomDocumentSettingsPanels(form)}
      />
      {form.headerEnabled && (
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.context.isLocked}
          panels={getCustomDocumentHeaderPanels(form)}
        />
      )}
      <DetailsSummaryList
        dispatch={form.dispatch}
        isLocked={form.context.isLocked}
        panels={getCustomDocumentBlockPanels(form)}
        canMove={constant(true)}
        onMove={form.moveBlock}
      />
      {when(prepAddBlockDropdown(form), (props) => (
        <DropdownMenu {...props} />
      ))}
      {form.signatureEnabled && (
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={form.context.isLocked}
          panels={getCustomDocumentSignaturePanels(form)}
        />
      )}
      {attachmentsPanel && <AttachmentsPanel {...attachmentsPanel} className="mt" />}
      {uploadAttachmentsButton && <SelectFilesButton {...uploadAttachmentsButton} />}
      <VBox gap={"md"} marginTop>
        {when(form.downloadSignedDocumentAction, (action) => (
          <Button
            variant={"secondary"}
            label={action.label}
            onClick={() => form.dispatch(action)}
          />
        ))}
        <ReopenDocumentButton context={form.context} dispatch={form.dispatch} />
        {!isDocumentSent(form.context, form.context.documentId) ? (
          <>
            <Button
              variant={"secondary"}
              label={getSendCustomDocumentTitle(form, lang)}
              onClick={() => form.openSendCustomDocumentDialog()}
            />
            {when(form.openSendForSigningDialogAction, (action) => (
              <Button
                variant={"secondary"}
                label={action.label}
                loading={isPending(form.context.documentSigners.get(form.context.documentId))}
                onClick={() => form.dispatch(action)}
              />
            ))}
          </>
        ) : form.hasNewRecipients && !form.isSentForSigning ? (
          <>
            <Alert
              icon
              type="warning"
              message={{
                key: "dbot.custom-document.send-to-new-recipients.info",
                params: { count: form.newRecipients.length },
              }}
            />
            <Button
              variant={"primary"}
              label={{
                key: "dbot.custom-document.send-to-new-recipients",
                params: { count: form.newRecipients.length },
              }}
              onClick={form.openSendToNewRecipientsDialog}
            />
          </>
        ) : undefined}
      </VBox>
      {form.sendDocumentDialogModel && (
        <SendCustomDocumentDialog model={form.sendDocumentDialogModel} />
      )}
    </VBox>
  );
});

export const CustomDocumentDetailsPanel = observer(
  ({ panel, form }: { panel: DetailsPanel; form: CustomDocumentForm }) => {
    switch (panel.id) {
      case "document-settings":
        return <CustomDocumentSettings form={form} />;
      case "recipients":
        return <CustomDocumentRecipientsPanel form={form} />;
      case "signature":
        return <CustomDocumentSignaturesPanel form={form} />;
      case "header":
        return <CustomDocumentHeaderPanel form={form} />;

      default: {
        const block = form.blocks.find((b) => b.id === panel.id);
        switch (block?.type) {
          case "freetext":
            return <CustomDocumentFreetextPanel form={form} block={block} />;
          case "shareholders":
            return <CustomDocumentShareholdersPanel form={form} block={block} />;
          case "transactionLog":
            return <CustomDocumentTransactionLogPanel form={form} block={block} />;
          case "personalDrawdownContribution":
            return <CustomDocumentPersonalDrawdownContributionPanel form={form} block={block} />;
          default:
            return null;
        }
      }
    }
  },
);

const CustomDocumentSettings = observer(({ form }: { form: CustomDocumentForm }) => {
  return (
    <VBox gap="lg">
      <DocumentLanguageSelector field={form.documentLanguage} isLocked={form.context.isLocked} />
      <LocalizedTextEditor
        model={form.title}
        useV2AutotranslateOption
        inputType={"string"}
        autofocus
      />
      {form.customDocumentTypeHint && (
        <Alert
          type="info"
          message={form.customDocumentTypeHint}
          onClose={form.clearCustomDocumentTypeHint}
        />
      )}
    </VBox>
  );
});

const CustomDocumentRecipients = observer(
  ({ recipients, alert }: { recipients: RecipientsEditModel; alert?: AlertProps }) => {
    const { t } = useTranslation();
    return recipients.isEmpty ? (
      <div>{t("none")}</div>
    ) : (
      <VBox>
        {recipients.recipientFields.map((r) => (
          <Box key={r.id} alignItems="start">
            <MTextInput
              model={r.nameField}
              placeholder={t("name")}
              autofocus
              disabled={isRecipientNameDisabled(r)}
            />
            <MTextInput model={r.emailField} placeholder={t("email")} />
            <IconButton
              icon={"trash"}
              tooltip={{ key: "delete" }}
              color={"error"}
              onClick={() => recipients.removeRecipient(r.id)}
            />
          </Box>
        ))}
        {when(alert, (props) => (
          <Alert {...props} />
        ))}
      </VBox>
    );
  },
);

const CustomDocumentRecipientsPanel = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();
  const companies = getProjectCompanies(form.context);

  const render = (orgNo: string, recipients: RecipientsEditModel | undefined) =>
    !recipients ? (
      <LoadingIndicator />
    ) : (
      <>
        <CustomDocumentRecipients recipients={recipients} alert={form.recipientsAlert} />
        <LinkButton
          className="self-start"
          icon={"plus"}
          label={{ key: "add-recipient" }}
          onClick={() => form.addNewRecipient(orgNo)}
        />
        <Box wrap>
          {quickAdd(form, orgNo).map(({ recipients, name, label, count, disabled }) => (
            <Button
              key={name}
              variant={"basic"}
              disabled={disabled}
              label={count !== undefined ? `${t(label)} (${count})` : t(label)}
              icon={"plus"}
              onClick={() => form.addRecipients(orgNo, recipients)}
              size={"sm"}
            />
          ))}
        </Box>
      </>
    );

  if (companies.length === 1) {
    return render(companies[0].orgNo, form.getRecipientsEditModel(companies[0].orgNo));
  }
  return (
    <VBox gap={false}>
      {companies.map((c) => {
        const recipients = form.getRecipientsEditModel(c.orgNo);
        return (
          <Details
            key={c.orgNo}
            title={`${c.name} (${recipients?.count ?? 0})`}
            open={form.selectedCompany === c.orgNo}
            onToggle={(open) => form.toggleCompany(c.orgNo, open)}
            bg={false}
            borderTop
            caretPosition="left"
            titleBoxProps={{ paddingX: false }}
            content={
              <VBox gap={"lg"} marginY={"lg"}>
                {render(c.orgNo, recipients)}
              </VBox>
            }
          />
        );
      })}
    </VBox>
  );
});

const CustomDocumentSignatoryList = observer(
  ({
    items,
    onMove,
    onRemove,
    isLocked,
  }: {
    items: CustomDocumentSignatoryForm[];
    onMove: SortableListProps<CustomDocumentSignatoryForm>["onMove"];
    onRemove: (item: CustomDocumentSignatoryForm) => void;
    isLocked?: boolean;
  }) => {
    const { t } = useTranslation();

    function render(form: CustomDocumentSignatoryForm) {
      switch (form.option) {
        case "freetext":
          return (
            <Box>
              <MTextInput model={form.freetextName} placeholder={t("name")} />
              <MTextInput model={form.freetextRole.textModels[0].model} placeholder={t("role")} />
            </Box>
          );
        default:
          return <Box>{t(signatoryOptionLabel[form.option])}</Box>;
      }
    }
    return (
      <SortableList items={items} onMove={onMove}>
        {items.map((s) => (
          <Sortable key={s.id} id={s.id} isDragDisabled={isLocked}>
            {(props) => (
              <Box alignItems="center">
                <Box htmlProps={{ ...props.dragAttributes, ...props.dragListeners }}>
                  <Icon name={"dots-six-vertical"} />
                </Box>
                <Box grow>{render(s)}</Box>
                {!isLocked && (
                  <IconButton
                    tooltip={{ key: "remove" }}
                    icon={"trash"}
                    color={"error"}
                    onClick={() => onRemove(s)}
                  />
                )}
              </Box>
            )}
          </Sortable>
        ))}
      </SortableList>
    );
  },
);

const CustomDocumentSignaturesPanel = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"xl"}>
      <VBox gap={"md"}>
        <strong>{t("dbot.custom-document.sender")}</strong>
        <LocalizedTextEditor
          model={form.signatureSendersStatement}
          placeholder={{ key: "dbot.custom-document.senders-statement.placeholder" }}
          useV2AutotranslateOption={showAutoTranslateOptions(form)}
        />
        <VBox>
          <CustomDocumentSignatoryList
            items={form.signatureSenders}
            onMove={(...args) => form.moveSignatory("signatureSenders", ...args)}
            onRemove={(...args) => form.removeSignatory("signatureSenders", ...args)}
            isLocked={form.context.isLocked}
          />
        </VBox>
        <div>
          {!form.context.isLocked && (
            <DropdownMenu
              button={{
                type: "link",
                icon: "plus",
                label: { key: "dbot.custom-document.add-sender" },
              }}
              items={prepAddSenderItems(form)}
            />
          )}
        </div>
      </VBox>
      <VBox gap={"md"}>
        <strong>{t("dbot.custom-document.signatories")}</strong>
        <LocalizedTextEditor
          model={form.signatoriesStatement}
          placeholder={{ key: "dbot.custom-document.signatories-statement.placeholder" }}
          useV2AutotranslateOption={showAutoTranslateOptions(form)}
        />
        <VBox>
          <CustomDocumentSignatoryList
            items={form.signatories}
            onMove={(...args) => form.moveSignatory("signatories", ...args)}
            onRemove={(...args) => form.removeSignatory("signatories", ...args)}
            isLocked={form.context.isLocked}
          />
        </VBox>
        <div>
          {!form.context.isLocked && (
            <DropdownMenu
              button={{
                type: "link",
                icon: "plus",
                label: { key: "dbot.custom-document.add-signatory" },
              }}
              items={prepAddSignatoryItems(form)}
            />
          )}
        </div>
      </VBox>
    </VBox>
  );
});

const CustomDocumentHeaderPanel = observer(({ form }: { form: CustomDocumentForm }) => {
  return (
    <VBox gap={"lg"}>
      <Checkbox
        label={{ key: "dbot.custom-document.logo" }}
        checked={form.headerLogoEnabled}
        onChange={(event) => form.set("headerLogoEnabled", event.target.checked)}
        disabled={form.context.isLocked}
      />
      <Checkbox
        label={{ key: "dbot.custom-document.company-name" }}
        checked={form.headerCompanyNameEnabled}
        onChange={(event) => form.set("headerCompanyNameEnabled", event.target.checked)}
        disabled={form.context.isLocked}
      />
      <VBox>
        <Checkbox
          label={{ key: "dbot.custom-document.inside-address" }}
          disabled={form.context.isLocked}
          checked={form.insideAddressEnabled}
          onChange={(e) => form.setInsideAddressEnabled(e.target.checked)}
        />
        <MSelect
          model={form.insideAddressOption}
          disabled={form.context.isLocked || !form.insideAddressEnabled}
        />
        {form.insideAddressOption.value === "freetext" && (
          <MTextInput
            placeholder={{ key: "name" }}
            model={form.insideAddressFreetext}
            disabled={form.context.isLocked || !form.insideAddressEnabled}
          />
        )}
      </VBox>
      <Checkbox
        label={{ key: "dbot.custom-document.document-title" }}
        checked={form.headerTitlesEnabled}
        onChange={(event) => form.set("headerTitlesEnabled", event.target.checked)}
        disabled={form.context.isLocked}
      />
      <VBox>
        <Checkbox
          label={{ key: "dbot.custom-document.document-date" }}
          checked={form.headerDateEnabled}
          onChange={(event) => form.set("headerDateEnabled", event.target.checked)}
          disabled={form.context.isLocked}
        />
        <MSelect
          model={form.documentDateOption}
          disabled={form.context.isLocked || !form.signatureEnabled}
        />
        {form.documentDateOption.value === "custom" && (
          <MDateInput
            model={form.customDocumentDate}
            disabled={form.context.isLocked || !form.signatureEnabled}
          />
        )}
      </VBox>
    </VBox>
  );
});

const CustomDocumentFreetextPanel = observer(
  ({ form, block }: { form: CustomDocumentForm; block: CustomDocumentFreetextBlockForm }) => {
    const showTranslationOptions = showAutoTranslateOptions(form);
    return (
      <VBox gap={"lg"}>
        <LocalizedTextEditor
          model={block.heading}
          inputType={"string"}
          placeholder={{ key: "optional" }}
          autofocus={autofocusHeading(block)}
          useV2AutotranslateOption={showTranslationOptions}
        />
        <LocalizedTextEditor
          model={block.text}
          autofocus={autofocusText(block)}
          useV2AutotranslateOption={showTranslationOptions}
        />
      </VBox>
    );
  },
);

const CustomDocumentShareholdersPanel = observer(
  ({ block, form }: { form: CustomDocumentForm; block: CustomDocumentShareholdersBlockForm }) => {
    return (
      <VBox>
        <MRadioButtonGroup
          horizontal
          model={block.currentOrPerDate}
          disabled={form.context.isLocked}
        />
        {block.showDateInput && (
          <MDateInput model={block.perDate} autofocus disabled={form.context.isLocked} />
        )}
      </VBox>
    );
  },
);

const CustomDocumentTransactionLogPanel = observer(
  ({ block, form }: { block: CustomDocumentTransactionLogBlockForm; form: CustomDocumentForm }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"lg"}>
        <LocalizedTextEditor
          model={block.heading}
          useV2AutotranslateOption
          inputType={"string"}
          placeholder={{ key: "optional" }}
        />
        <Box>
          <MDateInput
            model={block.from}
            label={t("from")}
            autofocus
            disabled={form.context.isLocked}
          />
          <MDateInput model={block.to} label={t("to")} autofocus disabled={form.context.isLocked} />
        </Box>
        <Box fg={"subtle"} textSize={"sm"}>
          {block.transactionTypes.options.length === 0
            ? t("dbot.transactions.info.noTransactionsTypes")
            : t("dbot.transactions.info.transactionsTypes")}
        </Box>
        <MCheckboxGroup model={block.transactionTypes} disabled={form.context.isLocked} />
      </VBox>
    );
  },
);

const CustomDocumentPersonalDrawdownContributionPanel = observer(
  ({
    block,
  }: {
    form: CustomDocumentForm;
    block: CustomDocumentPersonalDrawdownContributionBlockForm;
  }) => {
    return (
      <VBox>
        <Alert message={{ key: "dbot.drawdown.your-contribution.info" }} type={"info"} />
        <LocalizedTextEditor
          model={block.heading}
          useV2AutotranslateOption
          inputType={"string"}
          placeholder={{ key: "optional" }}
        />
      </VBox>
    );
  },
);

export type SendCustomDocumentModel = {
  recipients: RecipientsEditModel;
  title: (lang: Language) => TMessage | undefined;
  emailBody: LocalizedTextEditorModel;
  alert: () => AlertProps | undefined;
  isSubmitting?: boolean;
  onClose?: () => void;
  onSubmit: () => void;
};

export const SendCustomDocumentDialog = observer(
  ({ model }: { model: SendCustomDocumentModel }) => {
    const { t, lang } = useTranslation();
    return (
      <Dialog
        title={model.title(lang)}
        onCancel={model.onClose}
        onSubmit={model.onSubmit}
        isSubmitDisabled={!model.recipients.isValid}
        isSubmitting={model.isSubmitting}
        submitLabel={{ key: "send" }}
      >
        <VBox gap={"lg"}>
          <LocalizedTextEditor
            model={model.emailBody}
            placeholder={{ key: "customize-message.placeholder" }}
          />
          <VBox>
            <div>{t("dbot.recipients")}</div>
            <CustomDocumentRecipients recipients={model.recipients} alert={model.alert()} />
          </VBox>
          <div className="subtle">
            <Select
              label={t("dbot.archive-destination")}
              items={[]}
              idFn={String}
              labelFn={() => ({ key: "dbot.archive-destination.none" })}
              onSelect={noop}
              value={null}
              disabled
            />
          </div>
        </VBox>
      </Dialog>
    );
  },
);
